import type { FormatKey } from './priors.js'

export type CupEventLike = { targetTeamId: string; scorerId: string; second: number; isRedemption: boolean }

export type FactsInput = {
  homeTeamId: string
  awayTeamId: string
  cups: number
  /** In ordine di registrazione. */
  events: readonly CupEventLike[]
  /** Tiri a vuoto per squadra di chi ha tirato. */
  airballs: { home: number; away: number }
  rearrangements: number
  seconds: number
}

export type MatchFacts = {
  homeTeamId: string
  awayTeamId: string
  /** Punteggio come lo scrive il sistema: bicchieri contro l'avversario più tiri del pareggio segnati. */
  homeScore: number
  awayScore: number
  /** Bicchieri effettivamente presi all'avversario (un tiro del pareggio ne restituisce uno). */
  takenByHome: number
  takenByAway: number
  /** Ultimo bicchiere normale: 1 casa, −1 trasferta, 0 nessuno o subito dopo un pareggio. */
  last: -1 | 0 | 1
  firstCup: 'home' | 'away' | null
  firstScorer: string | null
  homePlayerCups: Record<string, number>
  awayPlayerCups: Record<string, number>
  /** Volte in cui una squadra è rimasta senza bicchieri (la perdente ha diritto al tiro del pareggio). */
  redemptionAttempts: number
  redemptionsMade: number
  /** Totale della partita; `homeAirballs`/`awayAirballs` la ripartizione per squadra di chi ha tirato. */
  airballs: number
  homeAirballs: number
  awayAirballs: number
  rearrangements: number
  seconds: number
}

const bump = (tally: Record<string, number>, id: string): Record<string, number> => ({ ...tally, [id]: (tally[id] ?? 0) + 1 })

/** Rigioca gli eventi in ordine: stessa logica dell'analisi dello storico (spec 4). */
export function matchFacts({ homeTeamId, awayTeamId, cups, events, airballs, rearrangements, seconds }: FactsInput): MatchFacts {
  let f: MatchFacts = {
    homeTeamId, awayTeamId, homeScore: 0, awayScore: 0, takenByHome: 0, takenByAway: 0, last: 0, firstCup: null, firstScorer: null,
    homePlayerCups: {}, awayPlayerCups: {}, redemptionAttempts: 0, redemptionsMade: 0,
    airballs: airballs.home + airballs.away, homeAirballs: airballs.home, awayAirballs: airballs.away, rearrangements, seconds,
  }
  for (const e of events) {
    const byHome = e.targetTeamId === awayTeamId
    f = byHome
      ? { ...f, homeScore: f.homeScore + 1, homePlayerCups: bump(f.homePlayerCups, e.scorerId) }
      : { ...f, awayScore: f.awayScore + 1, awayPlayerCups: bump(f.awayPlayerCups, e.scorerId) }
    if (f.firstCup === null) f = { ...f, firstCup: byHome ? 'home' : 'away', firstScorer: e.scorerId }
    if (e.isRedemption) {
      // Chi segna il pareggio si riprende un suo bicchiere: cala quanto ha preso l'avversario.
      f = byHome
        ? { ...f, takenByAway: Math.max(0, f.takenByAway - 1), redemptionsMade: f.redemptionsMade + 1, last: 0 }
        : { ...f, takenByHome: Math.max(0, f.takenByHome - 1), redemptionsMade: f.redemptionsMade + 1, last: 0 }
      continue
    }
    f = byHome ? { ...f, takenByHome: f.takenByHome + 1, last: 1 } : { ...f, takenByAway: f.takenByAway + 1, last: -1 }
    if ((byHome ? f.takenByHome : f.takenByAway) >= cups) f = { ...f, redemptionAttempts: f.redemptionAttempts + 1 }
  }
  return f
}

/** Bicchieri fatti/subiti e per giocatore; tiri a vuoto e secondi giocati per la propensione all'airball. */
export type TeamHistory = { made: number; conceded: number; playerCups: Record<string, number>; airballs: number; seconds: number }
export type TournamentTotals = { matches: number; seconds: number; cups: number; airballs: number; rearrangements: number; redemptionAttempts: number; redemptionsMade: number }
export type TournamentHistory = { teams: Map<string, TeamHistory>; totals: TournamentTotals }

const merge = (a: Record<string, number>, b: Record<string, number>): Record<string, number> =>
  Object.entries(b).reduce((acc, [id, n]) => ({ ...acc, [id]: (acc[id] ?? 0) + n }), a)

/** Somme del torneo sulle partite concluse: per squadra (bicchieri fatti/subiti, per giocatore) e totali. */
export function tournamentHistory(completed: readonly MatchFacts[]): TournamentHistory {
  const teams = new Map<string, TeamHistory>()
  const add = (teamId: string, made: number, conceded: number, playerCups: Record<string, number>, airballs: number, seconds: number) => {
    const t = teams.get(teamId) ?? { made: 0, conceded: 0, playerCups: {}, airballs: 0, seconds: 0 }
    teams.set(teamId, {
      made: t.made + made, conceded: t.conceded + conceded, playerCups: merge(t.playerCups, playerCups), airballs: t.airballs + airballs, seconds: t.seconds + seconds,
    })
  }
  let totals: TournamentTotals = { matches: 0, seconds: 0, cups: 0, airballs: 0, rearrangements: 0, redemptionAttempts: 0, redemptionsMade: 0 }
  for (const f of completed) {
    // Airball solo da partite con tempo: senza secondi non c'è esposizione (attesi = 0) e il moltiplicatore si gonfierebbe.
    const timed = f.seconds > 0
    add(f.homeTeamId, f.homeScore, f.awayScore, f.homePlayerCups, timed ? f.homeAirballs : 0, f.seconds)
    add(f.awayTeamId, f.awayScore, f.homeScore, f.awayPlayerCups, timed ? f.awayAirballs : 0, f.seconds)
    totals = {
      matches: totals.matches + 1,
      seconds: totals.seconds + f.seconds,
      cups: totals.cups + f.homeScore + f.awayScore,
      airballs: totals.airballs + f.airballs,
      rearrangements: totals.rearrangements + f.rearrangements,
      redemptionAttempts: totals.redemptionAttempts + f.redemptionAttempts,
      redemptionsMade: totals.redemptionsMade + f.redemptionsMade,
    }
  }
  return { teams, totals }
}

/**
 * Totali delle partite concluse raggruppati per formato: i ritmi (riposizionamenti, secondi per bicchiere,
 * tiri del pareggio, tiri a vuoto) di una finale a 6 bicchieri non vanno diluiti dalle partite a 3.
 */
export function totalsByFormat(completed: readonly { format: FormatKey; facts: MatchFacts }[]): Map<FormatKey, TournamentTotals> {
  const groups = new Map<FormatKey, MatchFacts[]>()
  for (const { format, facts } of completed) groups.set(format, [...(groups.get(format) ?? []), facts])
  return new Map([...groups].map(([format, list]) => [format, tournamentHistory(list).totals]))
}
